import { useUserStore } from "~/store/global/user.store";
import {
    changePasswordApi,
    checkResetPassApi,
    loginApi,
    otpRequestApi,
    otpVerifyApi,
    requestResetPassApi,
    signupApi,
    postToken,
} from "../api";
import type {
    ICheckForgetPassPayload,
    ILoginPayload,
    ILoginResponse,
    IOtpRequestPayload,
    IOtpVerifyPayload,
    IReqForgetPassPayload,
    ISignupPayload,
} from "../types";
import { usePermission } from "@vueuse/core";

export const useAuthStore = defineStore("auth", () => {
    const auth = useAuth();
    const { $clientPosthog } = useNuxtApp();

    // * component types to handle in auth module
    const { $token } = useNuxtApp();
    type TComponentTypes = "login" | "signup";

    // * states
    const authIsInprogress = ref(false);
    const socialAuthCompleted = ref(false);
    const componentTitle = ref("");
    const activeComponent = ref<TComponentTypes>("login");
    const hasEmailPermission = ref<boolean>(false);
    const { isUserLoggedIn } = storeToRefs(useUserStore());
    const showAuthModal = ref(false);
    // * computed properties
    const activeAuthForm = computed(() => {
        switch (activeComponent.value) {
            case "login":
                return defineAsyncComponent({
                    loader: () => import("../components/login/AppLogin.vue"),
                });

            case "signup":
                return defineAsyncComponent({
                    loader: () => import("../components/signup/AppSignup.vue"),
                });

            default:
                return defineAsyncComponent({
                    loader: () => import("../components/login/AppLogin.vue"),
                });
        }
    });

    // * methods
    const loginUser = async (payload: ILoginPayload) => {
        const { data, error } = await loginApi(payload);

        if (data.value) {
            const res = data.value as ILoginResponse;

            useCookie("access_token", {
                maxAge: res.expires_in,
            }).value = res.access_token;

            useCookie("refresh_token", {
                maxAge: res.refresh_expires_in,
            }).value = res.refresh_token;

            const currentDate = new Date();
            const futureTimestamp = currentDate.getTime() + res.expires_in * 1000;
            useCookie("token_expires_in", {
                maxAge: res.refresh_expires_in,
            }).value = futureTimestamp.toString();

            return Promise.resolve();
        } else {
            return Promise.reject(error.value);
        }
    };

    const signupUser = async (payload: ISignupPayload) => {
        const { data, error } = await signupApi(payload);

        if (data.value) {
            return Promise.resolve();
        } else {
            return Promise.reject(error.value);
        }
    };

    const otpRequest = async (payload: IOtpRequestPayload) => {
        const { data, error } = await otpRequestApi(payload);
        if (data.value) {
            return Promise.resolve();
        } else {
            return Promise.reject(error.value);
        }
    };

    const otpVerify = async (payload: IOtpVerifyPayload) => {
        const { data, error } = await otpVerifyApi(payload);

        if (data.value) {
            const res = data.value as ILoginResponse;
            $clientPosthog?.capture("aihd_user_signed_up");
            useCookie("access_token", {
                maxAge: res.expires_in,
            }).value = res.access_token;

            useCookie("refresh_token", {
                maxAge: res.refresh_expires_in,
            }).value = res.refresh_token;

            const currentDate = new Date();
            const futureTimestamp = currentDate.getTime() + res.expires_in * 1000;

            useCookie("token_expires_in", {
                maxAge: res.refresh_expires_in,
            }).value = futureTimestamp.toString();

            if (isUserLoggedIn.value) {
                return Promise.resolve();
            }
        } else {
            return Promise.reject(error.value);
        }
    };

    const requestResetPass = async (payload: IReqForgetPassPayload) => {
        const { data, error } = await requestResetPassApi(payload);

        if (data.value) {
            return Promise.resolve();
        } else {
            return Promise.reject(error.value);
        }
    };

    const checkResetPass = async (payload: ICheckForgetPassPayload) => {
        const { data, error } = await checkResetPassApi(payload);

        if (data.value) {
            return Promise.resolve();
        } else {
            return Promise.reject(error.value);
        }
    };

    const changePassword = async (payload: IOtpVerifyPayload) => {
        const { data, error } = await changePasswordApi(payload);

        if (data.value) {
            return Promise.resolve();
        } else {
            return Promise.reject(error.value);
        }
    };
    const notificationsAccess = usePermission("notifications");
    const sendToken = async () => {
        const payload = {
            fcm_token: $token,
            device_id: useCookie("device_id").value,
            notification_status: notificationsAccess.value,
        };

        await postToken(payload as any);
    };
    watch(
        auth,
        () => {
            if (auth.value && showAuthModal.value) {
                showAuthModal.value = false;
            }
        },
        { deep: true }
    );

    return {
        // * states
        componentTitle,
        activeComponent,
        hasEmailPermission,
        authIsInprogress,
        socialAuthCompleted,
        showAuthModal,
        // * methods
        otpVerify,
        loginUser,
        signupUser,
        otpRequest,
        checkResetPass,
        changePassword,
        requestResetPass,
        sendToken,

        // * computed
        activeAuthForm,
    };
});
